/**
 * Organization Preferences Hook - React Query Edition
 *
 * Manages organization-specific preferences (selected org, per-org settings)
 * using React Query with automatic localStorage persistence.
 *
 * Features:
 * - Selected organization tracking
 * - Per-organization negotiator mode
 * - Per-organization reports selection
 * - Per-organization reports insights cache
 * - Per-organization social deepdive collapsed state
 *
 * Usage:
 * const { selectedOrganizationId, setSelectedOrganization } = useOrganizationSelection();
 * const { negotiatorMode, setNegotiatorMode } = useNegotiatorMode(organizationId);
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { storage } from '@/lib/platform/utils/storage';
import { ORG_KEYS } from '@/lib/platform/constants/storage-keys';
import { logger } from '@/lib/platform/utils/logger';

// ============================================================================
// Organization Selection (selected_organization_id)
// ============================================================================

/**
 * Query key for selected organization
 */
const SELECTED_ORG_KEY = ['selected-organization'] as const;

/**
 * Hook to manage selected organization
 * SIMPLIFIED: Direct localStorage access with React state for reactivity
 */
export function useOrganizationSelection() {
  const queryClient = useQueryClient();

  // Store the selected organization ID in state (not just counter)
  const [selectedOrganizationId, setSelectedOrganizationIdState] = useState<string | null>(() => {
    // Migration: Move data from old key to new key
    const oldValue = storage.get<string>('selected_club_id');
    const newValue = storage.get<string>(ORG_KEYS.SELECTED_ORGANIZATION);

    if (oldValue && !newValue) {
      storage.set(ORG_KEYS.SELECTED_ORGANIZATION, oldValue);
      storage.remove('selected_club_id');
      return oldValue;
    }

    return newValue || null;
  });

  const setSelectedOrganization = (organizationId: string | null) => {
    logger.log('[useOrganizationSelection] setSelectedOrganization called with:', organizationId);
    logger.log(
      '[useOrganizationSelection] OLD localStorage value:',
      storage.get(ORG_KEYS.SELECTED_ORGANIZATION)
    );

    // Update localStorage synchronously
    if (organizationId) {
      storage.set(ORG_KEYS.SELECTED_ORGANIZATION, organizationId);
    } else {
      storage.remove(ORG_KEYS.SELECTED_ORGANIZATION);
    }

    logger.log(
      '[useOrganizationSelection] NEW localStorage value:',
      storage.get(ORG_KEYS.SELECTED_ORGANIZATION)
    );

    // Update React state to trigger re-render
    setSelectedOrganizationIdState(organizationId);

    // Also update React Query cache for compatibility
    queryClient.setQueryData(SELECTED_ORG_KEY, organizationId);

    logger.log('[useOrganizationSelection] Update completed, state updated to:', organizationId);
  };

  return {
    selectedOrganizationId: selectedOrganizationId || null,
    setSelectedOrganization,
    isLoading: false,
    isUpdating: false,
  };
}

// ============================================================================
// Per-Organization Negotiator Mode
// ============================================================================

/**
 * Query key factory for negotiator mode
 */
const negotiatorModeKey = (organizationId: string) => ['negotiator-mode', organizationId] as const;

/**
 * Hook to manage negotiator mode for a specific organization
 */
export function useNegotiatorMode(organizationId: string | undefined) {
  const queryClient = useQueryClient();

  // Query for negotiator mode (persisted to localStorage)
  const { data: negotiatorMode, isLoading } = useQuery({
    queryKey: negotiatorModeKey(organizationId || ''),
    queryFn: () => {
      if (!organizationId) return false;
      const value = storage.get<string>(ORG_KEYS.negotiatorMode(organizationId));
      return value === 'true';
    },
    enabled: !!organizationId,
    staleTime: Infinity,
    gcTime: Infinity,
    meta: {
      persist: true,
    },
  });

  // Mutation to toggle negotiator mode
  const updateMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      if (!organizationId) return false;
      storage.set(ORG_KEYS.negotiatorMode(organizationId), enabled ? 'true' : 'false');
      return enabled;
    },
    onSuccess: (enabled) => {
      if (organizationId) {
        queryClient.setQueryData(negotiatorModeKey(organizationId), enabled);
      }
    },
  });

  const setNegotiatorMode = (enabled: boolean) => {
    updateMutation.mutate(enabled);
  };

  const toggleNegotiatorMode = () => {
    updateMutation.mutate(!negotiatorMode);
  };

  return {
    negotiatorMode: negotiatorMode || false,
    setNegotiatorMode,
    toggleNegotiatorMode,
    isLoading,
    isUpdating: updateMutation.isPending,
  };
}

// ============================================================================
// Per-Organization Reports Selection
// ============================================================================

/**
 * Query key factory for reports selection
 */
const reportsSelectionKey = (organizationId: string) =>
  ['reports-selection', organizationId] as const;

/**
 * Hook to manage selected valuations for reports
 */
export function useReportsSelection(organizationId: string | undefined) {
  const queryClient = useQueryClient();

  // Query for reports selection (persisted to localStorage)
  const { data: selectedValuations, isLoading } = useQuery({
    queryKey: reportsSelectionKey(organizationId || ''),
    queryFn: () => {
      if (!organizationId) return [];
      return storage.get<string[]>(ORG_KEYS.reportsSelection(organizationId)) || [];
    },
    enabled: !!organizationId,
    staleTime: Infinity,
    gcTime: Infinity,
    meta: {
      persist: true,
    },
  });

  // Mutation to update reports selection
  const updateMutation = useMutation({
    mutationFn: async (valuationIds: string[]) => {
      if (!organizationId) return [];
      storage.set(ORG_KEYS.reportsSelection(organizationId), valuationIds);
      return valuationIds;
    },
    onSuccess: (valuationIds) => {
      if (organizationId) {
        queryClient.setQueryData(reportsSelectionKey(organizationId), valuationIds);
      }
    },
  });

  const setSelectedValuations = (valuationIds: string[]) => {
    updateMutation.mutate(valuationIds);
  };

  const clearSelection = () => {
    updateMutation.mutate([]);
  };

  return {
    selectedValuations: selectedValuations || [],
    setSelectedValuations,
    clearSelection,
    isLoading,
    isUpdating: updateMutation.isPending,
  };
}

// ============================================================================
// Per-Organization Reports Insights Cache
// ============================================================================

export interface ReportsInsights {
  valuationIds: string[];
  insights: string;
  timestamp: string;
}

/**
 * Query key factory for reports insights
 */
const reportsInsightsKey = (organizationId: string) =>
  ['reports-insights', organizationId] as const;

/**
 * Hook to manage reports insights cache
 */
export function useReportsInsights(organizationId: string | undefined) {
  const queryClient = useQueryClient();

  // Query for reports insights (persisted to localStorage)
  const { data: insights, isLoading } = useQuery({
    queryKey: reportsInsightsKey(organizationId || ''),
    queryFn: () => {
      if (!organizationId) return null;
      return storage.get<ReportsInsights>(ORG_KEYS.reportsInsights(organizationId));
    },
    enabled: !!organizationId,
    staleTime: Infinity,
    gcTime: Infinity,
    meta: {
      persist: true,
    },
  });

  // Mutation to update reports insights
  const updateMutation = useMutation({
    mutationFn: async (data: ReportsInsights | null) => {
      if (!organizationId) return null;
      if (data) {
        storage.set(ORG_KEYS.reportsInsights(organizationId), data);
      } else {
        storage.remove(ORG_KEYS.reportsInsights(organizationId));
      }
      return data;
    },
    onSuccess: (data) => {
      if (organizationId) {
        queryClient.setQueryData(reportsInsightsKey(organizationId), data);
      }
    },
  });

  const setInsights = (valuationIds: string[], insightsText: string) => {
    const data: ReportsInsights = {
      valuationIds,
      insights: insightsText,
      timestamp: new Date().toISOString(),
    };
    updateMutation.mutate(data);
  };

  const clearInsights = () => {
    updateMutation.mutate(null);
  };

  return {
    insights,
    setInsights,
    clearInsights,
    isLoading,
    isUpdating: updateMutation.isPending,
  };
}

// ============================================================================
// Per-Organization Social DeepDive Collapsed State
// ============================================================================

/**
 * Query key factory for social deepdive collapsed state
 */
const socialDeepDiveCollapsedKey = (organizationId: string) =>
  ['social-deepdive-collapsed', organizationId] as const;

/**
 * Hook to manage social deepdive collapsed platforms
 */
export function useSocialDeepDiveCollapsed(organizationId: string | undefined) {
  const queryClient = useQueryClient();

  // Query for collapsed platforms (persisted to localStorage)
  const { data: collapsedPlatforms, isLoading } = useQuery({
    queryKey: socialDeepDiveCollapsedKey(organizationId || ''),
    queryFn: () => {
      if (!organizationId) return [];
      return storage.get<string[]>(ORG_KEYS.socialDeepDiveCollapsed(organizationId)) || [];
    },
    enabled: !!organizationId,
    staleTime: Infinity,
    gcTime: Infinity,
    meta: {
      persist: true,
    },
  });

  // Mutation to update collapsed platforms
  const updateMutation = useMutation({
    mutationFn: async (platformIds: string[]) => {
      if (!organizationId) return [];
      storage.set(ORG_KEYS.socialDeepDiveCollapsed(organizationId), platformIds);
      return platformIds;
    },
    onSuccess: (platformIds) => {
      if (organizationId) {
        queryClient.setQueryData(socialDeepDiveCollapsedKey(organizationId), platformIds);
      }
    },
  });

  const setCollapsedPlatforms = (platformIds: string[]) => {
    updateMutation.mutate(platformIds);
  };

  const togglePlatform = (platformId: string) => {
    const current = collapsedPlatforms || [];
    const updated = current.includes(platformId)
      ? current.filter((id) => id !== platformId)
      : [...current, platformId];
    updateMutation.mutate(updated);
  };

  return {
    collapsedPlatforms: collapsedPlatforms || [],
    setCollapsedPlatforms,
    togglePlatform,
    isLoading,
    isUpdating: updateMutation.isPending,
  };
}
